import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  Kanban,
  MessageSquare,
  Phone,
  PhoneCall,
  RefreshCw,
  TrendingUp,
  Users,
  XCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { toastError } from '../../services/errorUtils'
import {
  formatDuration,
  getCallDisplayNumber,
  getStatusDotClass,
  getStatusKind,
  getStatusLabel
} from '../../utils/callPresentation'

interface DashboardStats {
  agents: number
  phoneNumbers: number
  totalCalls: number
  totalConversations: number
  activeConversations: number
  completedConversations: number
  successfulConversations: number
  failedConversations: number
  callDuration: number
  successRate: number
  conversationSuccessRate: number
  todayCalls: number
  weekCalls: number
  monthCalls: number
  avgCallDuration: number
  failedCalls: number
}

interface CallData {
  id: string
  agent_id: string
  agent_name?: string
  status: string
  status_kind?: string
  status_label?: string
  duration: number
  duration_label?: string
  created_at: string
  call_successful: string
  is_real_call: boolean
  display_phone_number?: string | null
  phone_number?: string | null
  phone_call?: {
    external_number?: string | null
    direction?: string | null
  } | null
  user_id?: string | null
  call_summary_title?: string | null
  direction?: string | null
}

interface AgentData {
  id: string
  name: string
  status: string
  phone_numbers: unknown[]
}

interface PhoneNumberData {
  id: string
  phone_number: string
  name: string
  status: string
  provider: string
}

interface TopAgentData {
  id: string
  name: string
  calls: number
  successRate: number
}

type ConnectionStatus = 'checking' | 'connected' | 'error'

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    agents: 0,
    phoneNumbers: 0,
    totalCalls: 0,
    totalConversations: 0,
    activeConversations: 0,
    completedConversations: 0,
    successfulConversations: 0,
    failedConversations: 0,
    callDuration: 0,
    successRate: 0,
    conversationSuccessRate: 0,
    todayCalls: 0,
    weekCalls: 0,
    monthCalls: 0,
    avgCallDuration: 0,
    failedCalls: 0
  })
  const [loading, setLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('checking')
  const [recentCalls, setRecentCalls] = useState<CallData[]>([])
  const [topAgents, setTopAgents] = useState<TopAgentData[]>([])
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const connectionToastShownRef = useRef(false)

  const showConnectionToastOnce = useCallback((type: 'success' | 'error', message: string) => {
    if (connectionToastShownRef.current) return
    toast[type](message)
    connectionToastShownRef.current = true
  }, [])

  const checkConnection = useCallback(async () => {
    try {
      setConnectionStatus('checking')
      const response = await api.get('/elevenlabs/test')
      if (response.data.success) {
        setConnectionStatus('connected')
        showConnectionToastOnce('success', 'Conectado ao ElevenLabs')
      } else {
        setConnectionStatus('error')
        showConnectionToastOnce('error', 'Falha na conexão com ElevenLabs')
      }
    } catch {
      setConnectionStatus('error')
      showConnectionToastOnce('error', 'Erro ao conectar com ElevenLabs')
    }
  }, [showConnectionToastOnce])

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      const [agentsRes, phoneNumbersRes, callsRes, conversationsRes] = await Promise.all([
        api.get('/agents'),
        api.get('/phone-numbers'),
        api.get('/calls/agent/all'),
        api.get('/conversations/agent/all')
      ])

      const agents: AgentData[] = agentsRes.data.data || []
      const phoneNumbers: PhoneNumberData[] = phoneNumbersRes.data.data || []
      const calls: CallData[] = callsRes.data.data || []
      const conversations: CallData[] = conversationsRes.data.data || []

      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

      const totalCalls = calls.length
      const callDuration = calls.reduce((acc, call) => acc + (call.duration || 0), 0)
      const avgCallDuration = totalCalls > 0 ? callDuration / totalCalls : 0
      const todayCalls = calls.filter(call => new Date(call.created_at) >= today).length
      const weekCalls = calls.filter(call => new Date(call.created_at) >= weekAgo).length
      const monthCalls = calls.filter(call => new Date(call.created_at) >= monthAgo).length
      const successfulCalls = calls.filter(call => getStatusKind(call) === 'success').length
      const failedCalls = calls.filter(call => getStatusKind(call) === 'failed').length
      const successRate = totalCalls > 0 ? (successfulCalls / totalCalls) * 100 : 0
      const successfulConversations = conversations.filter(conv => getStatusKind(conv) === 'success').length
      const activeConversationsCount = conversations.filter(conv => getStatusKind(conv) === 'active').length
      const completedConversations = conversations.filter(conv => getStatusKind(conv) === 'success').length
      const failedConversations = conversations.filter(conv => getStatusKind(conv) === 'failed').length
      const agentCallCounts = calls.reduce((acc, call) => {
        acc[call.agent_id] = (acc[call.agent_id] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      const topAgentsData = Object.entries(agentCallCounts)
        .map(([agentId, count]) => {
          const agent = agents.find(a => a.id === agentId)
          return {
            id: agentId,
            name: agent?.name || 'Agente sem nome',
            calls: count,
            successRate: (calls.filter(c => c.agent_id === agentId && getStatusKind(c) === 'success').length / count) * 100
          }
        })
        .sort((a, b) => b.calls - a.calls)
        .slice(0, 5)

      const recentCallsData = calls
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10)

      const conversationSuccessRate = conversations.length > 0
        ? (successfulConversations / conversations.length) * 100
        : 0

      setStats({
        agents: agents.length,
        phoneNumbers: phoneNumbers.length,
        totalCalls,
        totalConversations: conversations.length,
        activeConversations: activeConversationsCount,
        completedConversations,
        successfulConversations,
        failedConversations,
        callDuration,
        successRate: Math.round(successRate),
        conversationSuccessRate: Math.round(conversationSuccessRate),
        todayCalls,
        weekCalls,
        monthCalls,
        avgCallDuration: Math.round(avgCallDuration),
        failedCalls
      })
      setRecentCalls(recentCallsData)
      setTopAgents(topAgentsData)
      setLastUpdate(new Date())
    } catch (error) {
      toastError(error, 'Erro ao carregar dados do dashboard')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    checkConnection()
    loadDashboardData()
  }, [checkConnection, loadDashboardData])

  const connectionTone = getConnectionTone(connectionStatus)
  const callFailureRate = stats.totalCalls > 0 ? Math.round((stats.failedCalls / stats.totalCalls) * 100) : 0

  if (loading) {
    return (
      <div className="page-shell">
        {/* Skeleton page header */}
        <div className="page-header">
          <div className="space-y-2">
            <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-4 w-96 animate-pulse rounded-lg bg-slate-200" />
          </div>
          <div className="toolbar">
            <div className="h-10 w-40 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-10 w-28 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-10 w-28 animate-pulse rounded-lg bg-slate-200" />
          </div>
        </div>

        {/* Skeleton health bar */}
        <div className="surface overflow-hidden">
          <div className="grid gap-px bg-slate-200 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white p-4 space-y-2">
                <div className="h-3 w-28 animate-pulse rounded bg-slate-200" />
                <div className="h-6 w-20 animate-pulse rounded bg-slate-200" />
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton metric cards */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="metric-card">
              <div className="flex items-center gap-4">
                <div className="metric-icon bg-slate-100">
                  <div className="h-5 w-5 animate-pulse rounded bg-slate-200" />
                </div>
                <div className="min-w-0 space-y-2 flex-1">
                  <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                  <div className="h-8 w-16 animate-pulse rounded bg-slate-200" />
                  <div className="h-3 w-20 animate-pulse rounded bg-slate-200" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Skeleton panels row */}
        <div className="grid gap-6 xl:grid-cols-[1fr_1fr_24rem]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="surface">
              <div className="panel-header">
                <div className="h-5 w-32 animate-pulse rounded bg-slate-200" />
              </div>
              <div className="p-4 sm:p-5 space-y-3">
                {Array.from({ length: 4 }).map((_, j) => (
                  <div key={j} className="h-14 animate-pulse rounded-lg bg-slate-100" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-description">
            Visão geral da operação AICall, com ligações, agentes e conversas sincronizadas.
          </p>
        </div>

        <div className="toolbar">
          <div className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold ${connectionTone.pill}`}>
            {connectionTone.icon}
            <span>ElevenLabs {connectionTone.label}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              connectionToastShownRef.current = false
              checkConnection()
            }}
            className="btn-secondary"
          >
            <Activity className="h-4 w-4" />
            Verificar
          </button>
          <button type="button" onClick={loadDashboardData} className="btn-primary">
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
        </div>
      </div>

      <section className="surface overflow-hidden">
        <div className="grid gap-px bg-slate-200 md:grid-cols-4">
          <HealthTile label="Última atualização" value={lastUpdate.toLocaleTimeString('pt-BR')} />
          <HealthTile label="Chamadas hoje" value={stats.todayCalls} />
          <HealthTile label="Sucesso em chamadas" value={`${stats.successRate}%`} tone="success" />
          <HealthTile label="Falhas técnicas" value={`${callFailureRate}%`} tone={callFailureRate > 35 ? 'danger' : 'neutral'} />
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Users} label="Agentes" value={stats.agents} description="Total configurados" tone="primary" />
        <StatCard icon={Phone} label="Números" value={stats.phoneNumbers} description="Telefones ativos" tone="success" />
        <StatCard icon={PhoneCall} label="Total de ligações" value={stats.totalCalls} description={`${stats.todayCalls} hoje`} tone="warning" />
        <StatCard icon={MessageSquare} label="Conversas" value={stats.totalConversations} description={`${stats.successfulConversations} com sucesso`} tone="info" />
        <StatCard icon={Calendar} label="Esta semana" value={stats.weekCalls} description="ligações" tone="primary" />
        <StatCard icon={Clock} label="Duração média" value={formatDuration(stats.avgCallDuration)} description="por ligação" tone="success" />
        <StatCard icon={CheckCircle} label="Conversas ativas" value={stats.activeConversations} description="em andamento" tone="success" />
        <StatCard icon={TrendingUp} label="Conversas concluídas" value={stats.completedConversations} description="finalizadas" tone="info" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_1fr_24rem]">
        <Panel title="Top agentes" icon={<BarChart3 className="h-5 w-5 text-slate-400" />}>
          <div className="space-y-3">
            {topAgents.length > 0 ? (
              topAgents.map((agent, index) => (
                <div key={agent.id} className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 px-3 py-3 transition hover:bg-slate-50">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary-50 text-sm font-bold text-primary-700">
                      #{index + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">{agent.name}</p>
                      <p className="text-xs text-slate-500">{agent.calls} ligações</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums text-slate-950">{Math.round(agent.successRate)}%</p>
                    <p className="text-xs text-slate-500">sucesso</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <Users className="h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-500">Nenhum agente com ligações ainda</p>
                <p className="mt-1 text-xs text-slate-400">As estatísticas por agente aparecerão aqui.</p>
              </div>
            )}
          </div>
        </Panel>

        <Panel title="Ligações recentes" icon={<Clock className="h-5 w-5 text-slate-400" />}>
          {recentCalls.length > 0 ? (
            <div className="table-shell">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="table-head">
                  <tr>
                    <th scope="col" className="px-3 py-2.5 text-left">Número</th>
                    <th scope="col" className="px-3 py-2.5 text-left">Data</th>
                    <th scope="col" className="px-3 py-2.5 text-left">Status</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Duração</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {recentCalls.slice(0, 6).map(call => (
                    <RecentCallRow key={call.id} call={call} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <PhoneCall className="h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-500">Nenhuma ligação recente</p>
              <p className="mt-1 text-xs text-slate-400">As ligações recentes aparecerão aqui.</p>
            </div>
          )}
        </Panel>

        <Panel title="Ações rápidas" icon={<Activity className="h-5 w-5 text-slate-400" />}>
          <div className="space-y-3">
            <QuickActionButton icon={PhoneCall} label="Nova ligação" onClick={() => navigate('/app/calls')} primary />
            <QuickActionButton icon={Kanban} label="Abrir pipeline" onClick={() => navigate('/app/pipeline')} />
            <QuickActionButton icon={Users} label="Criar agente" onClick={() => navigate('/app/agents')} />
            <QuickActionButton icon={Phone} label="Adicionar número" onClick={() => navigate('/app/phone-numbers')} />
          </div>
        </Panel>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Panel title="Resumo de conversas">
          <div className="space-y-4">
            <ProgressRow label="Conversas com sucesso" value={stats.successfulConversations} percent={stats.conversationSuccessRate} tone="success" />
            <ProgressRow
              label="Conversas falharam"
              value={stats.failedConversations}
              percent={stats.totalConversations > 0 ? Math.round((stats.failedConversations / stats.totalConversations) * 100) : 0}
              tone="danger"
            />
            <ProgressRow label="Conversas ativas" value={stats.activeConversations} percent={stats.totalConversations > 0 ? Math.round((stats.activeConversations / stats.totalConversations) * 100) : 0} tone="info" />
          </div>
        </Panel>

        <Panel title="Estatísticas de ligações">
          <dl className="grid gap-3 sm:grid-cols-2">
            <StatLine label="Hoje" value={stats.todayCalls} />
            <StatLine label="Esta semana" value={`${stats.weekCalls} ligações`} />
            <StatLine label="Este mês" value={`${stats.monthCalls} ligações`} />
            <StatLine label="Duração média" value={formatDuration(stats.avgCallDuration, true)} />
            <StatLine label="Taxa de sucesso" value={`${stats.successRate}%`} />
            <StatLine label="Falhas" value={`${stats.failedCalls} chamadas`} />
          </dl>
        </Panel>
      </section>
    </div>
  )
}

function getConnectionTone(status: ConnectionStatus) {
  switch (status) {
    case 'connected':
      return {
        label: 'conectado',
        icon: <CheckCircle className="h-4 w-4" />,
        pill: 'border-success-200 bg-success-50 text-success-700'
      }
    case 'error':
      return {
        label: 'com erro',
        icon: <XCircle className="h-4 w-4" />,
        pill: 'border-danger-200 bg-danger-50 text-danger-700'
      }
    default:
      return {
        label: 'verificando',
        icon: <Clock className="h-4 w-4 animate-spin" />,
        pill: 'border-warning-200 bg-warning-50 text-warning-700'
      }
  }
}

interface PanelProps {
  title: string
  icon?: ReactNode
  children: ReactNode
}

function Panel({ title, icon, children }: PanelProps) {
  return (
    <section className="surface">
      <div className="panel-header">
        <h2 className="panel-title">{title}</h2>
        {icon}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  )
}

interface StatCardProps {
  icon: ComponentType<{ className?: string }>
  label: string
  value: string | number
  description: string
  tone: 'primary' | 'success' | 'warning' | 'info'
}

const statToneClass: Record<StatCardProps['tone'], string> = {
  primary: 'bg-primary-50 text-primary-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  info: 'bg-sky-50 text-sky-700'
}

function StatCard({ icon: Icon, label, value, description, tone }: StatCardProps) {
  return (
    <div className="metric-card">
      <div className="flex items-center gap-4">
        <div className={`metric-icon ${statToneClass[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-1 truncate text-3xl font-bold tabular-nums text-slate-950">{value}</p>
          <p className="mt-1 text-xs text-slate-400">{description}</p>
        </div>
      </div>
    </div>
  )
}

function HealthTile({ label, value, tone = 'neutral' }: { label: string; value: string | number; tone?: 'neutral' | 'success' | 'danger' }) {
  const valueClass = tone === 'success' ? 'text-success-700' : tone === 'danger' ? 'text-danger-700' : 'text-slate-950'

  return (
    <div className="bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${valueClass}`}>{value}</p>
    </div>
  )
}

function RecentCallRow({ call }: { call: CallData }) {
  const statusKind = getStatusKind(call)
  const statusLabel = getStatusLabel(statusKind)

  const badgeClass =
    statusKind === 'success'
      ? 'badge-success'
      : statusKind === 'failed'
        ? 'badge-danger'
        : statusKind === 'active'
          ? 'badge-info'
          : 'badge bg-slate-100 text-slate-600'

  return (
    <tr className="transition hover:bg-slate-50">
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 flex-none rounded-full ${getStatusDotClass(statusKind)}`} />
          <span className="truncate text-sm font-semibold text-slate-950">{getCallDisplayNumber(call)}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">
        {new Date(call.created_at).toLocaleString('pt-BR')}
      </td>
      <td className="px-3 py-2.5">
        <span className={badgeClass}>{statusLabel}</span>
      </td>
      <td className="px-3 py-2.5 text-right text-xs font-medium tabular-nums text-slate-600 whitespace-nowrap">
        {formatDuration(call.duration)}
      </td>
    </tr>
  )
}

function QuickActionButton({
  icon: Icon,
  label,
  onClick,
  primary = false
}: {
  icon: ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  primary?: boolean
}) {
  return (
    <button type="button" onClick={onClick} className={`w-full ${primary ? 'btn-primary' : 'btn-secondary'}`}>
      <Icon className="h-4 w-4" />
      {label}
    </button>
  )
}

function ProgressRow({ label, value, percent, tone }: { label: string; value: number; percent: number; tone: 'success' | 'danger' | 'info' }) {
  const toneClass = tone === 'success' ? 'bg-success-500' : tone === 'danger' ? 'bg-danger-500' : 'bg-primary-500'

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-600">{label}</span>
        <span className="text-sm font-semibold tabular-nums text-slate-950">{value} ({percent}%)</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all ${toneClass}`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
      </div>
    </div>
  )
}

function StatLine({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums text-slate-950">{value}</dd>
    </div>
  )
}
